import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { locationLine } from "@/lib/format";
import { PROFILE_TYPE_LABEL } from "@/lib/constants";
import { TagList } from "./Tag";
import PortfolioButtons from "./PortfolioButtons";

/** Reusable profile card for musician, band, DJ, venue. No rate/pricing on profiles. */
export default function ProfileCard({ profile }) {
  const { account, loading: authLoading } = useAuth();
  const isVenue = profile.profile_type === "venue";
  const href = `/profiles/view/${profile.profile_id}`;
  const handleProfileClick = (event) => {
    // While the session is loading, let the protected route decide based on the final auth state.
    if (authLoading || account) return;
    event.preventDefault();
    toast.info("Please sign in to view full profiles. Use the Sign in button to continue.");
  };
  return (
    <article data-testid={`profilecard-${profile.profile_id}`}
      className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all">
      <Link to={href} onClick={handleProfileClick} className="block">
        <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
          {profile.profile_picture_url && (
            <img src={profile.profile_picture_url} alt={profile.display_name}
              className="w-full h-full object-cover" />
          )}
        </div>
      </Link>
      <div className="p-4">
        <Link to={href} onClick={handleProfileClick} className="block">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#4F46E5]">
            {PROFILE_TYPE_LABEL[profile.profile_type]}
          </div>
          <div className="mt-1 font-semibold text-slate-900 truncate">{profile.display_name}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {locationLine(profile) || "—"}
            {isVenue && profile.venue_type ? ` · ${profile.venue_type}` : null}
          </div>
        </Link>
        <div className="mt-3">
          <TagList items={profile.genres} max={3} />
        </div>
        {(profile.instruments?.length > 0) && (
          <div className="mt-2">
            <TagList items={profile.instruments} max={3} tone="accent" />
          </div>
        )}
        <div className="mt-4 flex items-end justify-between gap-3">
          {isVenue && profile.capacity ? (
            <div className="text-sm font-medium text-slate-900">Capacity {profile.capacity}</div>
          ) : <div />}
          <PortfolioButtons profile={profile} size="sm" />
        </div>
      </div>
    </article>
  );
}
